import { fmtD } from "./dateFormat";
// ─────────────────────────────────────────────────────────────────────────
// Shared look & feel for the CRM module (Pipeline, Contacts, Tasks, P&L).
// Mirrors the existing dashboard/tracker styling so it feels native to
// the app instead of bolted on.
// ─────────────────────────────────────────────────────────────────────────

export const ACCENT = "#6c63ff";
export const ACCENT_LIGHT = "#8b7cff";

export const STAGES = [
  "New Lead",
  "Contacted",
  "Meeting/Demo",
  "Proposal Sent",
  "Negotiation",
  "Won",
  "Lost",
];

export const STAGE_COLOR = {
  "New Lead": "#7b82a0",
  "Contacted": "#4f8ef7",
  "Meeting/Demo": "#f0b429",
  "Proposal Sent": "#f59e42",
  "Negotiation": "#e879f9",
  "Won": "#3ecf8e",
  "Lost": "#f87171",
};

export const CONTACT_ROLES = ["President", "Secretary", "Treasurer", "Committee Member", "Other"];

export const PNL_CATEGORIES = [
  "Legal / Registration", "Trademark / Patent", "Technology",
  "Marketing", "Salary", "Server / Infra", "Consulting", "Other",
];

export const PRIORITY_COLOR = { High: "#f87171", Medium: "#fbbf24", Low: "#3ecf8e" };

export const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return fmtD(d);
};

export const todayStr = () => new Date().toISOString().slice(0, 10);

// Shared style object — same conventions as MyTrackerScreen.jsx's `S`.
export const S = {
  wrap: { padding: "16px", fontFamily: "sans-serif", color: "#e8eaf0" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "8px" },
  title: { fontSize: "18px", fontWeight: "700", color: "#a78bfa" },
  metricRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px", marginBottom: "20px" },
  metricCard: (accent) => ({
    background: "#1a1d27", border: "1px solid #2e3250", borderRadius: "10px", padding: "14px",
    borderTop: `3px solid ${accent || ACCENT}`,
  }),
  metricLbl: { fontSize: "11px", color: "#7b82a0", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "6px" },
  metricVal: (color) => ({ fontSize: "20px", fontWeight: "700", color: color || "#e8eaf0", fontFamily: "monospace" }),
  metricSub: { fontSize: "11px", color: "#7b82a0", marginTop: "3px" },
  section: { fontSize: "12px", fontWeight: "700", color: "#7b82a0", textTransform: "uppercase", letterSpacing: "0.8px", margin: "20px 0 10px" },
  card: { background: "#1a1d27", border: "1px solid #2e3250", borderRadius: "10px", padding: "16px" },
  tableWrap: { background: "#1a1d27", border: "1px solid #2e3250", borderRadius: "10px", overflow: "hidden", marginBottom: "16px" },
  th: { padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#7b82a0", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #2e3250", background: "#222638", textAlign: "left" },
  td: { padding: "11px 14px", fontSize: "13px", borderBottom: "1px solid #2e3250", verticalAlign: "middle" },
  form: { background: "#1a1d27", border: "1px solid #2e3250", borderRadius: "10px", padding: "16px", marginBottom: "16px" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", marginBottom: "14px" },
  label: { display: "block", fontSize: "11px", color: "#7b82a0", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "5px" },
  input: { width: "100%", background: "#222638", border: "1px solid #2e3250", borderRadius: "7px", color: "#e8eaf0", fontSize: "13px", padding: "8px 10px", outline: "none", boxSizing: "border-box" },
  select: { width: "100%", background: "#222638", border: "1px solid #2e3250", borderRadius: "7px", color: "#e8eaf0", fontSize: "13px", padding: "8px 10px", outline: "none", boxSizing: "border-box" },
  textarea: { width: "100%", background: "#222638", border: "1px solid #2e3250", borderRadius: "7px", color: "#e8eaf0", fontSize: "13px", padding: "8px 10px", outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: "sans-serif" },
  btnBlue: { background: ACCENT, color: "#fff", border: "none", borderRadius: "7px", padding: "9px 18px", fontSize: "13px", fontWeight: "600", cursor: "pointer" },
  btnGray: { background: "#222638", color: "#ccc", border: "1px solid #2e3250", borderRadius: "7px", padding: "9px 14px", fontSize: "13px", cursor: "pointer" },
  btnRed: { background: "rgba(248,113,113,0.15)", color: "#f87171", border: "1px solid rgba(248,113,113,0.3)", borderRadius: "7px", padding: "5px 10px", fontSize: "11px", cursor: "pointer" },
  btnSmall: { background: "#222638", color: "#aaa", border: "1px solid #2e3250", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer" },
  msg: { background: "#222638", border: `1px solid ${ACCENT}`, borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", fontSize: "13px", color: ACCENT_LIGHT },
  badge: (color) => ({
    display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "600",
    background: `${color}22`, color, border: `1px solid ${color}55`,
  }),
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px" },
  modalBox: { background: "#181b26", border: "1px solid #2e3250", borderRadius: "12px", padding: "22px", width: "100%", maxWidth: "520px", maxHeight: "90vh", overflow: "auto" },
};